import Core        from "@/core/Core";
import AclDAO      from '@/core/services/Acl/AclDAO';
import User        from "@/core/services/Acl/User";
import Partner     from "@/domains/Partners/Partner";
import router      from "@/router";
import RpcResponse from "@/core/services/Rpc/RpcResponse";
import DialogsService from "@/core/services/Dialogs/DialogsService";

interface AclData {
    sessionToken: string | null;
    currentUser: User;
    currentPartner: Partner;
}

/**
 * Сервис управления доступом и авторизацией
 * Содержит бизнес-логику авторизации, управления сессией и проверки прав доступа
 * Использует AclDAO для взаимодействия с сервером
 */
class AclService {
    public static isInitialized = false;

    static get dao() {
        return AclDAO;
    }

    /**
     * Приватные данные приложения
     */
    private static data: AclData = reactive({
        sessionToken: null,
        currentUser: null,
        currentPartner: null,
    });

    /**
     * Инициализация сервиса
     * @returns {Promise<void>} Инициализированный сервис
     */
    static async init(): Promise<void> {
        if (this.isInitialized) {
            Core.error('AclService уже инициализирован');
            return;
        }
        // Проверяем сессию при инициализации сервиса
        await this.checkSession();

        this.isInitialized = true;
				Core.eventBus.AclReady.emit();
        //Core.debug('🔑 AclService инициализирован');
    }

    /**
     * Геттер текущего пользователя
     * @returns Данные текущего пользователя
     */
    static get currentUser() {
        return this.data.currentUser;
    }


    /**
     * Проверка активной сессии пользователя при запуске приложения
     * @returns {Promise<boolean>} true если сессия действительна
     */
    static async checkSession(): Promise<boolean> {
        this.data.sessionToken = this.getStoredSessionToken();
        if (!this.sessionToken) {
            Core.debug('Токен сессии отсутствует');
            return false;
        }
        // проверяем валиден ли наш токен, получая данные пользователя
        /** Ошибка запроса здесь ожидаема: токен может быть протухшим или битым.
         * updateCurrentUserData пробрасывает исключение дальше, и без перехвата
         * оно уходило в Core.initialize и обрывало инициализацию приложения,
         * не дав отработать handleInvalidSession */
        try {
            await this.updateCurrentUserData();
        } catch (error) {
            Core.debug('Не удалось получить пользователя по сохранённому токену: ', error);
        }

        if (this.currentUser) {
            Core.eventBus.AclSessionRestored.emit();
            Core.debug('Сессия восстановлена');
            return true;
        } else {
            await this.handleInvalidSession();
            return false;
        }
    }

    /**
     * Авторизация пользователя
     * @param email - Email пользователя
     * @param password - Пароль пользователя
     * @param code - Код авторизации
     * @returns Данные пользователя
     */
    static async login(email: string, password: string | null, code: string | null = null): Promise<void> {
        try {
            const result = await this.dao.login(email, password, code);
            if (result.data && result.meta?.sessionToken) {
                // Сначала установлю токен, потому что в setUserData нужно сделать запрос партнёра уже с токеном
                this.setSessionToken(result.meta.sessionToken);
                this.setUserData(result.data as User);
                // Генерируем событие успешного входа
                Core.eventBus.AclLogin.emit();
            } else {
                throw new Error('Неверные данные ответа сервера');
            }
        } catch (error) {
            Core.debug('Ошибка авторизации: ', error);
            throw error;
        }
    }

    /**
     * Отправка кода авторизации на email
     * @param email - Email пользователя
     * @returns Результат отправки кода
     */
    static async sendLoginCode(email: string): Promise<any> {
        return this.dao.sendLoginCode(email);
    }

		/**
		 * Напоминание пароля на email
		 * @param email - Email пользователя
		 * @returns true or false
		 */
		static async sendPassword(email: string): Promise<Boolean> {
			return this.dao.sendPassword(email);
		}


    /**
     * Смена пароля пользователя
     * FIXME: Эндпоинт не требует текущий пароль - проблема безопасности
     * @param newPassword - Новый пароль
     * @returns Результат смены пароля
     */
    static async changePassword(newPassword: string): Promise<boolean> {
        const response = await this.dao.changePassword(newPassword) as RpcResponse;

				if (!response.ok) {
					Core.showNotification.error("Ошибка при обновлении пароля");
				}

				if (response.ok) {
					Core.showNotification.success('Пароль успешно изменен');
				}

				return response.data;
    }

    /**
     * Выход из системы
     */
    static async logout(): Promise<void> {
        /** Логика в том, что мы сначала перебрасываем пользователя
         * на страницы логина, а только потом сбрасбываем состояние
         * Делается это по причине того, что если сначала начать сбрасывать состояние
         * то некоторые компоненты ререндарятся сразу и падают с ошибкой, что мол
         * в текущем пользователе больше нет displayCurrencyCode and so on, да
         * и вообще пользователя нет
         *
         * Почему нельзя вызвать метод из vuex - потому что в событийной системе
         * у меня нет возможности управлять слушателем этого события. Мне
         * нужно отследить переход на страницу логина, и только после этого
         * сбрасывать состояние
         * */

        await router.push('/login');
        DialogsService.hideAll();

        // Уведомляем сервер о выходе (если нужно)
        await this.dao.logout();
        this.clearSessionData();
        this.removeStoredSessionToken();

        // Генерируем событие выхода
				Core.eventBus.AclLogout.emit();
    }

    /**
     * Получение текущего пользователя с сервера
     * @returns Данные пользователя
     */
    static async updateCurrentUserData(): Promise<void> {
        try {
            const userData = await this.dao.getCurrentUser();
            if (userData) {
                this.setUserData(userData);
            }
        } catch (error) {
            Core.error('Ошибка получения данных пользователя:', error);
            throw error;
        }
    }

    /**
     * Обновление отображаемой валюты
     * @param currencyCode - Код валюты
     * @returns Результат сохранения
     */
    static async updateDisplayedCurrencyCode(currencyCode: string): Promise<void> {
        try {
            await this.dao.saveDisplayedCurrencyCode(currencyCode);
            await this.updateCurrentUserData();
        } catch (error) {
            Core.error('Ошибка обновления отображаемой валюты: ', error);
            throw error;
        }
    }

    static setSessionToken(token: string): void {
        this.data.sessionToken = token;
        this.storeSessionToken(token);
    }

    /**
     * Получение токена сессии из localStorage
     * @returns Токен сессии
     */
    private static getStoredSessionToken(): string | null {
        return localStorage.getItem('sessionToken');
    }

    /**
     * Сохранение токена сессии в localStorage
     * @param token - Токен сессии
     */
    private static storeSessionToken(token: string): void {
        localStorage.setItem('sessionToken', token);
    }

    /**
     * Удаление токена сессии из localStorage
     */
    private static removeStoredSessionToken(): void {
        localStorage.removeItem('sessionToken');
    }

    /**
     * Установка данных пользователя
     * @param userData - Данные пользователя
     */
    private static async setUserData(userData: any): void {
        this.data.currentUser = User.fromServer(userData);

        /** логиниться мы можем с локальной таймзоной, а у пользователя
         * на самом деле может быть другая таймзона. Точно также
         * как и при выходе из под одного пользователя, и заходе под другого **/
        if (this.data.currentUser?.timeZone) {
            Core.setTimeZone(this.data.currentUser.timeZone);
        }

        this.data.currentPartner = Partner.fromServer(userData.partner)
    }

    /**
     * Очистка данных пользователя
     */
    private static clearSessionData(): void {
        this.data.sessionToken = null;
        this.data.currentUser = null;
        this.data.currentPartner = null;
    }

    /**
     * Обработка невалидной сессии
     */
    static async handleInvalidSession(): Promise<void> {
        /** Проверяем токен, а не пользователя: при битом токене пользователь так и не
         * был установлен, и по прежнему условию метод не делал ничего - токен оставался
         * в localStorage, а событие не отправлялось.
         * Условие сохранено, чтобы не отправлять событие повторно: после очистки
         * токена уже нет */
        if (this.sessionToken) {
            Core.debug('Сессия недействительна');

            /** Сначала уводим на логин, потом сбрасываем состояние - по той же причине,
             * что и в logout: уже смонтированные компоненты читают currentPartner в
             * mounted и падают, если обнулить его под ними.
             * Раньше здесь очистка шла без перехода, и протухшая сессия давала пачку
             * ошибок вида "currentPartner is null" с отправкой их в Sentry */
            await router.push('/login');
            DialogsService.hideAll();

            this.clearSessionData();
            this.removeStoredSessionToken();
						Core.eventBus.AclSessionExpired.emit();
        }
    }

    /**
     * Геттер токена сессии
     * @returns Токен текущей сессии или null если пользователь не авторизован
     */
    static get sessionToken(): string | null {
        return this.data.sessionToken;
    }

    /**
     * Геттер текущего партнера
     * @returns Данные текущего партнера
     */
    static get currentPartner(): Partner {
        return this.data.currentPartner;
    }

    /**
     * Геттер валюты текущего партнера
     * Добавил геттер потому что ну уж очень долго писать постоянно путь длинной в 5 вложенностей
     * @returns Данные текущего партнера
     */
    static get currentPartnerCurrencyCode(): string {
        return this.data.currentPartner?.country?.localCurrency?.code || '';
    }

    /**
     * Геттер отображаемой на сайте валюты
     * @returns Код валюты
     */
    static get displayedCurrencyCode(): string {
        return this.data.currentUser?.displayCurrencyCode || '';
    }

    /**
     * Проверка авторизации пользователя
     * @returns true если пользователь авторизован
     */
    static get isAuthenticated(): boolean {
        return !!(this.data.sessionToken && this.data.currentUser);
    }
}

export default AclService;
